import { useMemo, useState } from 'react';
import { Button, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { Invoice } from '../../utils/types';
import { useAppContext } from '../../context/AppContext';
import { useDownloadInvoice, useInvoices } from '../../hooks/payment';
import { downloadFile } from '../../api';
import StatusTag from '../Properties/components/StatusTag';
import { formatDate } from './Subscription';
import ChoiceDropdown from '../../components/ChoiceDropdown';

export function downloadInvoice(
  url: string,
  domain: string,
  onProgress?: (progress: number) => void,
) {
  return downloadFile(url, `${domain}-invoice.pdf`, onProgress);
}

const INVOICE_STATUS_OPTIONS = [
  { label: 'Paid', value: 'paid' },
  { label: 'Failed', value: 'failed' },
  { label: 'Pending', value: 'pending' },
];

const Invoices = () => {
  const { pCode } = useAppContext();
  const { data: invoices, isPending } = useInvoices(pCode);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [downloadingInvoices, setDownloadingInvoices] = useState<Set<string>>(new Set());

  const { mutateAsync: downloadInvoiceMutation } = useDownloadInvoice(() => {});

  const handleDownload = async (invoiceNumber: string, domain: string) => {
    // Mark as downloading
    setDownloadingInvoices((prev) => new Set(prev).add(invoiceNumber));

    try {
      const data = await downloadInvoiceMutation({ invoiceId: invoiceNumber });
      
      // Now download the actual file and wait for it to complete
      await downloadFile(data.downLoadUrl, `${domain}-invoice.pdf`);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      // Remove downloading state after download completes (or fails)
      setTimeout(() => {
        setDownloadingInvoices((prev) => {
          const newSet = new Set(prev);
          newSet.delete(invoiceNumber);
          return newSet;
        });
      }, 500);
    }
  };

  const getColumns = (): ColumnsType<Invoice> => [
    {
      title: 'Domain/Property Name',
      dataIndex: 'domain',
      key: 'domainProperty',
      width: 200,
    },
    {
      title: 'Billing Date',
      dataIndex: 'billingDate',
      key: 'billingDate',
      width: 150,
      render: (text: string) => <span>{formatDate(new Date(text))}</span>,
    },
    {
      title: 'Invoice No.',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNo',
      width: 150,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      render: (text: string, record: Invoice) => (
        <span>
          {record.currencyCode} {text}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string, record: Invoice) => (
        <>
          <StatusTag
            status={status?.toLowerCase() as 'paid' | 'failed' | 'pending'}
          />
          {['failed'].includes(status?.toLowerCase() ?? '') && (
            <Tooltip title={record.failureReason || 'Unknown reason'}>
              <InfoCircleOutlined
                style={{ color: 'red', marginLeft: '8px' }}
              />
            </Tooltip>
          )}
        </>
      ),
    },
    {
      title: '',
      key: 'download',
      width: 150,
      render: (_, record) => {
        const isCurrentlyDownloading = downloadingInvoices.has(record.invoiceNumber);
        return (
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record.invoiceNumber, record.domain)}
            disabled={isCurrentlyDownloading}
            loading={isCurrentlyDownloading}
          >
            Download Invoice
          </Button>
        );
      },
    },
  ];

  const columns = useMemo(() => getColumns(), [downloadingInvoices]);

  const filteredDataSource = useMemo(() => {
    const data = Array.isArray(invoices) ? invoices : [];
    if (selectedStatuses.length === 0) {
      return data;
    }
    return data.filter((invoice: Invoice) => {
      const status = invoice.status?.toLowerCase();
      return selectedStatuses.some(
        (selectedStatus) => selectedStatus.toLowerCase() === status,
      );
    });
  }, [invoices, selectedStatuses]);

  const handleStatusChange = (values: string[]) => {
    setSelectedStatuses(values);
  };

  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <ChoiceDropdown
          options={INVOICE_STATUS_OPTIONS}
          onChange={handleStatusChange}
          placeholder="Filter by Status"
          mode="tags"
        />
      </div>
      <Table
        columns={columns}
        dataSource={Array.isArray(filteredDataSource) ? filteredDataSource : []}
        rowKey="invoiceNumber"
        loading={isPending}
        scroll={{ x: 1000 }}
      />
    </>
  );
};

export default Invoices;
